import React, { useMemo } from 'react';
import { Avatar, Flex, Tooltip } from 'antd';

import styles from '../Lead/index.less';
import classNames from 'classnames';
import languages from '@/components/Kanban/ServiceRequests/Calendar/languages';
import { checkNullString } from '@/utils/prettyname';
import { useOverflowTooltip } from '@/utils/hooks';
import { AssigneeModel } from '@/typings/models/Assignee';
import { AgentActionTag } from '@/components/ActionTag';
import { useModel } from '@@/exports';
import { useIntl } from 'umi';
import { F } from '@/utils/intl';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';

export const isAgentActive = (agent: AssigneeModel) => {
  if (!agent) return true; // Default to true if agent is undefined/null
  if (typeof agent.active === 'boolean') return agent.active;
  if (typeof agent.active === 'string') return agent.active == '1';
  return agent.active;
};
const getAgentName = (agent: AssigneeModel) => {
  if (agent?.fullname) return agent.fullname;
  if (!agent || (!agent.firstname && !agent.lastname)) return null;
  return `${checkNullString(agent.firstname)} ${checkNullString(agent.lastname)}`;
};

const AgentName = ({
                     agent,
                     hideAvatar,
                     hideInactiveFlag,
                     justAvatar,
                     nameClassName,
                     avatarClassName,
                     showFlag,
                     className,
                     clickable,
                     showNameInTooltip,
                     size,
                   }: {
  agent: AssigneeModel,
  nameClassName?: string,
  avatarClassName?: string,
  className?: string,
  hideAvatar?: boolean,
  hideInactiveFlag?: boolean,
  showNameInTooltip?: boolean,
  clickable?: boolean,
  showFlag?: boolean,
  justAvatar?: boolean,
  size?: string,
  readonly?: boolean
}) => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const user = initialState.currentUser?.User;
  const intl = useIntl();
  const isMe = agent?.id === user?.alias;
  let name = getAgentName(agent);
  if (!name) name = ' - ';
  const overflownTooltip = useOverflowTooltip(name);

  const isActive = useMemo(() => hideInactiveFlag ? true : isAgentActive(agent), [agent?.active]);
  const avatar = (
    <Avatar size={'small'} src={agent.avatar} className={classNames(styles.leadNameAvatar, avatarClassName)}>
      {name.slice(0, 2)}
    </Avatar>
  );

  // Return avatar only if justAvatar prop is true
  if (justAvatar) return <Tooltip title={showNameInTooltip && name}>
    {avatar}
  </Tooltip>;

  if (isEmpty(agent)) return ' - ';

  const content = <div className={classNames(styles.editableTextWrapper, className, {
    [styles.inactiveAgent]: !isActive,
  })}>
    <Flex gap={5} align={'center'}>
      {!hideAvatar && !agent.unassigned && avatar}
      <span className={classNames(nameClassName)}>
        {overflownTooltip}
        {showFlag && <Tooltip showArrow={false} placement={'bottomLeft'} title={languages['en-GB'].label}>
          {languages['en-GB'].icon}
        </Tooltip>}
      </span>
      {isMe &&
        `(${intl.formatMessage({
          id: 'common.me',
          defaultMessage: 'Me',
        })}) `}
      {!isActive && !agent.unassigned && <small>
        <F id="common.inactive" defaultMessage={'(Inactive)'} />
      </small>}
    </Flex>
  </div>;

  if (clickable) return <AgentActionTag agent={agent} size={size} />;
  return content;
};

const propsAreEqual = (prevProps, nextProps) => {
  const fieldsToCheck = ['firstname', 'lastname', 'email', 'avatar', 'active', 'color'];
  const previousAgent = pick(prevProps.agent, fieldsToCheck);
  const nextAgent = pick(nextProps.agent, fieldsToCheck);
  return isEqual(previousAgent, nextAgent);
};

export default React.memo(AgentName, propsAreEqual);
